import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings, Save, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailResendConfig() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState({
    api_key: '',
    from_email: '',
    from_name: ''
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/email-campaigns/config/resend`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data) {
        setConfig(response.data);
        setFormData({
          api_key: '',
          from_email: response.data.from_email,
          from_name: response.data.from_name
        });
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.api_key || !formData.from_email || !formData.from_name) {
      toast.error('Todos los campos son requeridos');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/email-campaigns/config/resend`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Configuración guardada exitosamente');
      navigate('/email-campaigns');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <Button
          onClick={() => navigate('/email-campaigns')}
          variant="ghost"
          className="mb-4 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Settings className="h-8 w-8 text-indigo-600" />
          Configuración de Resend
        </h1>
        <p className="text-gray-600 mt-2">
          Configura tu API Key de Resend para enviar emails
        </p>
      </div>

      {/* Info Card */}
      <Card className="mb-6 border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-2">¿Cómo obtener tu API Key?</p>
              <ol className="list-decimal list-inside space-y-1 text-blue-800">
                <li>Visita <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline">resend.com</a> y crea una cuenta</li>
                <li>Ve a "API Keys" en el panel</li>
                <li>Crea una nueva API Key</li>
                <li>Verifica tu dominio de email en Resend</li>
                <li>Pega tu API Key aquí</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Config */}
      {config && (
        <Card className="mb-6 border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-900">
              <CheckCircle className="h-5 w-5" />
              Configuración Actual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium text-green-900">Email remitente:</span>
                <span className="ml-2 text-green-800">{config.from_email}</span>
              </div>
              <div>
                <span className="font-medium text-green-900">Nombre remitente:</span>
                <span className="ml-2 text-green-800">{config.from_name}</span>
              </div>
              <div>
                <span className="font-medium text-green-900">API Key:</span>
                <span className="ml-2 text-green-800 font-mono">{config.api_key_preview}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>
            {config ? 'Actualizar Configuración' : 'Nueva Configuración'}
          </CardTitle>
          <CardDescription>
            Ingresa tus credenciales de Resend
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="api_key">API Key de Resend *</Label>
              <Input
                id="api_key"
                type="password"
                placeholder="re_xxxxxxxxxxxxx"
                value={formData.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                required
                className="mt-1 font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Tu API Key debe comenzar con "re_"
              </p>
            </div>

            <div>
              <Label htmlFor="from_email">Email Remitente *</Label>
              <Input
                id="from_email"
                type="email"
                placeholder="noreply@tudominio.com"
                value={formData.from_email}
                onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                required
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Debe ser un email verificado en Resend
              </p>
            </div>

            <div>
              <Label htmlFor="from_name">Nombre Remitente *</Label>
              <Input
                id="from_name"
                type="text"
                placeholder="Mi Agencia de Seguros"
                value={formData.from_name}
                onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                required
                className="mt-1"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={saving}
                className="gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Guardar Configuración
                  </>
                )}
              </Button>
              
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/email-campaigns')}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
